import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  Button,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from "@mui/material";
import { pricingContainer } from "./PricingStyles";
import SubscriptionCarousel from "./Components/SubscriptionCarousel";
import { useDispatch, useSelector } from "react-redux";
import SubscriptionComparisonTable from "./Components/SubscriptionComparisonTable";
import colors from "../../../Constants/colors";

import { useNavigate } from "react-router";

import downIcon from "../../../Assets/down.png";
import axios from "axios";
import { saveCancelStatus } from "../../../features/organization/orgSlice";
import theme from "../../../Constants/theme";
import {
  deleteButtonStyles,
  secondaryButtonStyles,
} from "../../../Constants/buttonStyles";
import { dialogStyles } from "../../../Constants/dialogStyles";

const PricingPage = () => {
  const [loadCheckout, setLoadCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [plan, setPlan] = useState("");
  const { user, token } = useSelector((state) => state.auth);
  const { organization } = useSelector((state) => state.organization);
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (organization?.subscription) {
      switch (organization.subscription) {
        case "starter":
          setPlan("starter");
          break;
        case "pro":
          setPlan("pro");
          break;
        case "enterprise":
          setPlan("enterprise");
          break;
        default:
          setPlan(""); // Handle any other cases if needed
      }
    }
  }, [organization?.subscription]);

  const tableRef = useRef(null); // Reference to the table
  // Function to scroll to the table
  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const [options, setOptions] = useState(null);

  const subscriptionRoute = async (subscribedPlan) => {
    setLoading(true);
    if (organization?.subscription === "pro" && subscribedPlan === "pro") {
      setOpenConfirm(true);
    } else if (
      organization?.subscription === "starter" &&
      subscribedPlan === "pro"
    ) {
      await handleSubscribe(subscribedPlan);
    }

    setLoading(false);
  };

  const handleCancel = async () => {
    const org_id = organization?._id["$oid"];
    try {
      if (!org_id || !token) {
        setMessage(
          "Error: Invalid organization id, please refresh the page and try again. "
        );
        setOpenAlert(true);
        setOpenConfirm(false);
        throw new Error("Invalid organization id.");
      }
      const response = await axios.get(
        `/api/payments/subscribe/cancel/${org_id}`,
        {
          org_id: org_id,
        }
      );

      // {
      //   headers: {
      //     Authorization: `Bearer ${token.replaceAll('"', "").trim()}`, // Include JWT for authentication
      //   },

      // }
      const data = await response?.data?.subscription?.cancel_at_period_end;
      if (data) {
        setMessage("Subscription successfully canceled.");
        setOpenAlert(true);
        dispatch(saveCancelStatus("active"));
        setOpenConfirm(false);
      }
    } catch (error) {
      console.log(`Error happened while canceling subscription: ${error}`);
      setOpenConfirm(false);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (subscribedPlan) => {
    localStorage.setItem("package", subscribedPlan);
    setLoading(true);
    const userId = user?.user?._id["$oid"];
    const org_id = organization?._id["$oid"];

    try {
      if (!org_id) {
        setMessage(
          "Error: Invalid organization id, please refresh the page and try again. "
        );
        setOpenAlert(true);
        throw new Error("Organization ID is invalid!");
      }
      const response = await axios.post(
        `/api/payments/subscribe/${subscribedPlan}`,
        {
          org_id: org_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Include JWT for authentication
          },
        }
      );

      if (response.data) {
        window.open(response.data);
      } else {
        setErrorMessage(
          "Error: Failed to initiate payment. Please contact support for assistance."
        );
        setOpenAlert(true);
      }
    } catch (error) {
      setErrorMessage(
        "Error: Failed to initiate payment. Please contact support for assistance."
      );
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const subscriptionData = [
    {
      title: "Starter",
      price: "Free",
      buttonText: "Trial Version",
      features: [
        "Limited retrieval optimization. ",
        "Limited generation optimization.",
        "Support for limited foundation models.",
        "Support for structured and unstructured data.",
        "Natural language database queries.",
        "Ability to create a single application",
        "Set your own PineCone and OpenAI keys to remove limitations on the number of files, hyperparameter combinations, and benchmark answers.",
      ],
    },
    {
      title: "QueryLoop Pro",
      price: "USD 499 per month",
      buttonText:
        organization?.subscription === "pro"
          ? organization?.subscription_cancellation_status === "active"
            ? "Your subscription will be canceled at the end of the billing cycle"
            : "Cancel subscription"
          : "Subscribe",
      features: [
        "Retrieval optimization including identification of the optimal chunking strategy, embedding options, distance metrics, query preprocessing, and reranking methods.",
        "Generation optimization including identification of the best prompts and the most suitable LLMs.",
        "Support for major foundation models.",
        "Automatic embedding and LLM fine-tuning with Grid search and Bayesian optimization.",
        "Support for structured and unstructured data.",
        "Natural language database queries.",
        "Metadata filtering",
        "Access to beta features.",
      ],
    },
    {
      title: "QueryLoop Enterprise",
      price: "Custom Plan",
      buttonText: "Contact Us",
      features: [
        "Access to all Features",
        "Extensive Custom Support by top LLM experts",
      ],
    },
  ];

  return (
    <Box
      sx={{
        ...pricingContainer,
        width: "100%",
        padding: "20px",
      }}
    >
      {/* <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        fullWidth
      >
        <div
          style={{
            backgroundColor: colors.extremeBlue,
            padding: "20px",
            color: colors.bgLight,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p style={{ fontSize: "20px" }}>
            Are you sure want to cancel the subscription?
          </p>
          <div>
            <button
              style={{
                backgroundColor: "var(--colorDanger)",
                border: "none",
                padding: "13px",
                cursor: "pointer",
                color: "var(--bgLight)",
                marginRight: "10px",
                fontSize: "20px",
              }}
              onClick={handleCancel}
            >
              Yes
            </button>
            <button
              style={{
                backgroundColor: "var(--buttonBackground)",
                border: "none",
                padding: "13px",
                cursor: "pointer",
                color: "var(--bgLight)",
                fontSize: "20px",
              }}
              onClick={() => setOpenConfirm(false)}
            >
              No
            </button>
          </div>
        </div>
      </Dialog> */}

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        fullWidth
        PaperProps={{
          sx: {
            ...dialogStyles.dialogRoot,
          },
        }}
      >
        <DialogTitle sx={dialogStyles.dialogTitle}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent dividers sx={dialogStyles.dialogContent}>
          <DialogContentText sx={dialogStyles.dialogContentText}>
            Are you sure you want to cancel the subscription?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ ...dialogStyles.dialogActions }}>
          <Button onClick={handleCancel} sx={deleteButtonStyles}>
            Yes
          </Button>
          <Button
            onClick={() => setOpenConfirm(false)}
            sx={secondaryButtonStyles}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openAlert}
        autoHideDuration={6000}
        onClose={() => {
          setOpenAlert(false);
        }}
      >
        <Alert
          variant="filled"
          severity={
            message.startsWith("Error:")
              ? "error"
              : message.startsWith("Info:")
              ? "warning"
              : "success"
          }
        >
          {message.replace("Info:", "")}
        </Alert>
      </Snackbar>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {successMessage && (
            <Typography color="success">{successMessage}</Typography>
          )}
          {errorMessage && (
            <Typography color="error">{errorMessage}</Typography>
          )}

          <SubscriptionCarousel
            cards={subscriptionData}
            setPlan={setPlan}
            handleSubscribe={subscriptionRoute}
            plan={plan}
          />
          <Box
            component="img"
            src={downIcon}
            alt="Down Arrow"
            onClick={scrollToTable}
            sx={{
              cursor: "pointer",
              width: "50px", // Adjust size as needed
              height: "50px",
              display: "block",
              margin: "auto",
              filter:
                "invert(23%) sepia(100%) saturate(2462%) hue-rotate(236deg) brightness(100%) contrast(101%)",
            }}
          />
          <Divider
            sx={{ backgroundColor: colors.extremeWhite, marginBottom: "20px" }}
          />
          <Box ref={tableRef} sx={{ width: "100%" }}>
            <SubscriptionComparisonTable />
          </Box>
        </>
      )}
    </Box>
  );
};

export default PricingPage;
